/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import {
  InstantSearch, connectSearchBox, connectHits, connectStats, connectStateResults,
} from 'react-instantsearch-dom'
import { isEmpty, compose } from 'ramda'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import CoachSearchResultCard from '@components/coaches-search/CoachResults/CoachSearchResult'
import { DirectoryHydrateContext } from '@components/coaches-search/CoachResults/directory-hydrate-context'
import defaults from '@config/defaults'
import { makeGaFriendlyObj, shouldShowEventsBanner } from '@components/coaches-search/CoachResults/utils'
import useDebounce from '@hooks/useDebounce'
import useSavedCoaches from '@hooks/useSavedCoaches'
import { useBookThisWeekDirectory } from '@hooks/useCoachesBookThisWeek'
import JsonLd from '@components/atoms/json-ld'
import { gtmEvent } from '@lib/gtm-utils'
import { parseAroundLatLng } from '@lib/algolia/coach-geoloc'
import {
  buildCoachDirectoryProfessionalServiceJsonLd,
  getCoachDirectoryDocumentSeo,
} from '@lib/pga-coach/search-seo'
import EventCardBanner from '@components/event-card-banner'
import Box from '@material-ui/core/Box'
import { LocateFixed } from 'lucide-react'
import {
  detectAroundLatLngFromIp,
  placeNameFromAroundLatLng,
} from '@lib/pga-coach/detect-location-from-ip'
import CoachAccreditationExplainerDialog from '@components/coach-accreditation-explainer-dialog'
import ConfigureWithLocation from './ConfigureWithLocation'
import { useStyles } from './CoachesSearch.styles'
import DirectoryHeaderPanel from './components/directory-header-panel'
import DirectoryPageHero from './components/directory-page-hero'
import { getResultsCountLine } from './verified-members-summary'

const PGA_MEMBER_ACCREDITATION_ID = 'pga-member'
export const IP_LOCATION_HYDRATE_TIMEOUT_MS = 2000

function normalizeAroundLatLng(value) {
  return String(value || '').replace(/\s/g, '')
}

function searchResultsMatchPinnedLatLng(searchResults, aroundLatLng) {
  if (!aroundLatLng || !searchResults || searchResults.__isArtificial) return false
  const resultLatLng = searchResults._state?.aroundLatLng
  if (!resultLatLng) return false
  return normalizeAroundLatLng(resultLatLng) === normalizeAroundLatLng(aroundLatLng)
}

const resultsStringFor = (term, place) => {
  let resultString = ''
  if (term && place) {
    resultString = `Results for "${term}" near "${place}"`
  } else if (term) {
    resultString = `Results for "${term}"`
  } else if (place) {
    resultString = `golf instructor near "${place}"`
  }
  return resultString
}

const isCompletePlace = (placeName) => placeName.includes(',')

export function SearchResults(props) {
  const [placeSearchValue, setPlaceSearchValue] = useState('')
  const [sendImpressionEvent, setSendImpressionEvent] = useState(true)
  const router = useRouter()

  const {
    currentRefinement,
    refine,
    hits,
    refinerData,
    placeName,
    searchState,
    nbHits,
    onPersonaChange,
    persona,
    bookThisWeek,
    onToggleBookThisWeek,
    searchResults,
    searching,
    isSearchStalled,
    setAroundLatLng,
    aroundLatLng,
    onClearDirectoryFilters,
    bookThisWeekJoinReady = true,
  } = props
  const setAroundLatLngRef = useRef(setAroundLatLng)
  setAroundLatLngRef.current = setAroundLatLng
  const [isLocationHydrating, setIsLocationHydrating] = useState(
    () => !placeName && !aroundLatLng,
  )
  const awaitingPinnedSearchRef = useRef(false)
  const searchCenter = parseAroundLatLng(aroundLatLng)
  const selectedPersonaId = persona || null
  // InstantSearch connectors compare extra props with react-fast-compare
  // (functions by identity). A new clear/persona callback each render makes
  // connectInfiniteHits / connectCurrentRefinements call widgetsManager.update()
  // and onSearchStateChange, which re-renders this parent and livelocks the
  // directory. Keep the identities stable and read latest values from refs.
  const onPersonaChangeRef = useRef(onPersonaChange)
  onPersonaChangeRef.current = onPersonaChange
  const onClearDirectoryFiltersRef = useRef(onClearDirectoryFilters)
  onClearDirectoryFiltersRef.current = onClearDirectoryFilters
  const onToggleBookThisWeekRef = useRef(onToggleBookThisWeek)
  onToggleBookThisWeekRef.current = onToggleBookThisWeek
  const selectedPersonaIdRef = useRef(selectedPersonaId)
  selectedPersonaIdRef.current = selectedPersonaId

  const handlePersonaChange = useCallback((nextPersonaId, previousPersonaId) => {
    gtmEvent({
      event: 'coach-directory-popular-goal-select',
      attributes: {
        persona: nextPersonaId,
        previousPersona: previousPersonaId,
      },
    })
    onPersonaChangeRef.current?.(nextPersonaId)
  }, [])
  const handleClearDirectoryFilters = useCallback(() => {
    if (selectedPersonaIdRef.current) {
      gtmEvent({
        event: 'coach-directory-popular-goal-select',
        attributes: {
          persona: null,
          previousPersona: selectedPersonaIdRef.current,
        },
      })
    }
    onClearDirectoryFiltersRef.current?.()
  }, [])
  const handleToggleBookThisWeek = useCallback(() => {
    onToggleBookThisWeekRef.current?.()
  }, [])
  const classes = useStyles()
  const debouncedPlaceName = useDebounce(placeSearchValue, 1500)
  const debouncedsearchState = useDebounce(searchState, 1500)
  const [shouldShowBanner, setShouldShowBanner] = useState(false)
  const [openAccreditationId, setOpenAccreditationId] = useState(null)
  const closeMembershipExplainer = useCallback(() => setOpenAccreditationId(null), [])

  // Saved-coaches state is purely client-side (localStorage) and survives
  // across visits without auth. Filtering happens in the infinite-hits
  // connector below — Algolia still drives the underlying query so geo /
  // text refinement keeps working; saved-only just hides non-matching hits
  // from the rendered list.
  const {
    savedIdSet,
    toggle: toggleSavedCoach,
    isCoachSaved,
  } = useSavedCoaches()
  const [savedOnly, setSavedOnly] = useState(false)
  const handleToggleSavedOnly = useCallback(() => {
    setSavedOnly((prev) => {
      const next = !prev
      gtmEvent({
        event: 'coach-directory-saved-coaches-filter-toggle',
        attributes: {
          toValue: next ? '1' : '0',
          fromValue: prev ? '1' : '0',
          savedCount: savedIdSet.size,
        },
      })
      return next
    })
  }, [savedIdSet.size])

  const handleToggleSavedCoach = useCallback((coachId) => {
    const nextSaved = toggleSavedCoach(coachId)
    gtmEvent({
      event: 'coach-directory-coach-favorite-toggle',
      attributes: {
        coachId,
        toValue: nextSaved ? '1' : '0',
      },
    })
  }, [toggleSavedCoach])

  useEffect(() => {
    if (savedOnly && savedIdSet.size === 0) {
      setSavedOnly(false)
    }
  }, [savedOnly, savedIdSet.size])

  useEffect(() => {
    if (placeName) {
      setPlaceSearchValue(placeName)
    }
    const getBannerData = async () => {
      const showBanner = await shouldShowEventsBanner()
      if (showBanner) {
        setShouldShowBanner(true)
      }
    }
    getBannerData()
  }, [])

  // Pin InstantSearch to the visitor IP point without waiting on a City, ST
  // label. Client-only so Algolia sees the visitor, not the SSR host.
  // Reverse-geocode fills Near + the count in a follow-up effect. Canonical
  // stays the unscoped `/coaches/search` path. Stay in the hydrating wait
  // until InstantSearch `_state.aroundLatLng` matches the pin — clearing as
  // soon as the pinned search *starts* would still paint leftover host ViaIP
  // hits.
  useEffect(() => {
    if (placeName || aroundLatLng || typeof setAroundLatLngRef.current !== 'function') {
      if (!awaitingPinnedSearchRef.current) {
        setIsLocationHydrating(false)
      }
      return undefined
    }
    let ignore = false
    let timeoutId
    setIsLocationHydrating(true)
    const hydrateFromIp = async () => {
      try {
        const coordsPromise = detectAroundLatLngFromIp()
        coordsPromise.catch(() => {})
        const aroundLatLngFromIp = await Promise.race([
          coordsPromise,
          new Promise((_, reject) => {
            timeoutId = window.setTimeout(
              () => reject(new Error('ip-location-hydrate-timeout')),
              IP_LOCATION_HYDRATE_TIMEOUT_MS,
            )
          }),
        ])
        if (ignore) return
        if (aroundLatLngFromIp) {
          awaitingPinnedSearchRef.current = true
          setIsLocationHydrating(true)
          setAroundLatLngRef.current(aroundLatLngFromIp, undefined, { syncUrl: false })
          return
        }
        awaitingPinnedSearchRef.current = false
        setIsLocationHydrating(false)
      } catch (error) {
        // Directory still works with ViaIP and "coaches available".
        if (!ignore) {
          awaitingPinnedSearchRef.current = false
          setIsLocationHydrating(false)
        }
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId)
      }
    }
    hydrateFromIp()
    return () => {
      ignore = true
      if (timeoutId) window.clearTimeout(timeoutId)
    }
  }, [aroundLatLng, placeName])

  // City, ST is not on the card-paint path. Fill Near + the count after the
  // pin so a slow Coach Tools reverse-geocode cannot keep the skeleton up.
  useEffect(() => {
    if (!aroundLatLng || isCompletePlace(placeName || '')) return undefined
    if (typeof setAroundLatLngRef.current !== 'function') return undefined
    let ignore = false
    placeNameFromAroundLatLng(aroundLatLng)
      .then((nextPlaceName) => {
        if (ignore || !nextPlaceName) return
        setAroundLatLngRef.current(aroundLatLng, nextPlaceName, { syncUrl: false })
      })
      .catch(() => {})
    return () => {
      ignore = true
    }
  }, [aroundLatLng, placeName])

  // Leftover ViaIP `searching` is not the pinned query. Keep hydrating until
  // InstantSearch is idle with `_state.aroundLatLng` matching the pin. The 2s
  // timeout applies only to detectAroundLatLngFromIp — fail-open after a pin
  // would paint leftover host ViaIP hits.
  useEffect(() => {
    if (!awaitingPinnedSearchRef.current || !aroundLatLng) return undefined

    const inFlight = Boolean(searching || isSearchStalled)
    const pinnedAndIdle = !inFlight && searchResultsMatchPinnedLatLng(
      searchResults,
      aroundLatLng,
    )
    if (!pinnedAndIdle) return undefined

    awaitingPinnedSearchRef.current = false
    setIsLocationHydrating(false)
    return undefined
  }, [searching, isSearchStalled, aroundLatLng, placeName, searchResults])

  useEffect(() => {
    if (searching) return
    if (!isEmpty(debouncedsearchState) || (isCompletePlace(debouncedPlaceName)) || persona || bookThisWeek) {
      const searchWithPlace = {
        ...debouncedsearchState,
        ...(isCompletePlace(debouncedPlaceName) && { placeName: debouncedPlaceName }),
        ...(persona && { persona }),
        ...(bookThisWeek && { bookThisWeek: true }),
      }

      // TODO: Eventually send the GA object and the AMP object.
      // const ampObj = makeAmpFriendlyObj(searchWithPlace, refinerSectionMap)
      const searchObj = makeGaFriendlyObj(searchWithPlace, refinerData)
      const numberOfPlusUsersInCurrentSearch = (hits || []).filter((hit) => hit.hasPlusSubscription).length

      if (sendImpressionEvent) {
        gtmEvent({
          event: 'view-coach-search-optimized-directory',
          attributes: { coachSearchQueryHits: nbHits, ...searchObj },
        })
        setSendImpressionEvent(false)
      }
      gtmEvent({
        event: 'coachSearchQuery',
        attributes: { coachSearchQueryHits: nbHits, numberOfPlusUsersInCurrentSearch, ...searchObj },
      })
    } else if (sendImpressionEvent) {
      gtmEvent({
        event: 'view-coach-search-optimized-directory',
        attributes: { coachSearchQueryHits: null, coachSearchQuery: null },
      })
      setSendImpressionEvent(false)
    }
  }, [debouncedsearchState, debouncedPlaceName, persona, bookThisWeek, searching])

  const {
    title: metaTitle,
    description: metaDescription,
    canonicalUrl,
    breadcrumbList,
  } = getCoachDirectoryDocumentSeo({
    placeName,
    asPath: router?.asPath,
    baseUrl: defaults.baseUrl,
  })

  const displayedCoachCount = savedOnly
    ? (hits || []).filter((hit) => isCoachSaved(hit.objectID)).length
    : Number(nbHits) || 0
  const savedCoachCountInResults = (hits || []).filter(
    (hit) => isCoachSaved(hit.objectID),
  ).length
  const shouldShowResultsCount = displayedCoachCount > 0
  const resultsCountLine = getResultsCountLine({
    displayedCoachCount,
    hits,
    searchResults,
    savedOnly,
    isCoachSaved,
    placeName,
  })

  return (
    <Container maxWidth="xl" className={classes.root}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalUrl,
        }}
      />
      <JsonLd {...breadcrumbList} />
      {hits && hits.length ? hits.map((hit) => (
        <JsonLd
          key={hit.objectID}
          {...buildCoachDirectoryProfessionalServiceJsonLd(hit, defaults.baseUrl, { searchCenter })}
        />
      )) : null}

      <DirectoryPageHero />
      <DirectoryHeaderPanel
        placeName={placeName}
        selectedPersonaId={selectedPersonaId}
        onPersonaChange={onPersonaChange ? handlePersonaChange : null}
        onClearDirectoryFilters={onClearDirectoryFilters ? handleClearDirectoryFilters : null}
        onLocationSelect={setAroundLatLng}
        savedOnly={savedOnly}
        onToggleSavedOnly={handleToggleSavedOnly}
        savedCoachCount={savedCoachCountInResults}
        refinerData={refinerData}
        bookThisWeek={bookThisWeek}
        onToggleBookThisWeek={onToggleBookThisWeek ? handleToggleBookThisWeek : undefined}
      />
      <Grid container spacing={4}>
        {shouldShowResultsCount && resultsCountLine && (
          <Grid item xs={12} className={classes.resultsCountGridItem}>
            <Box className={classes.resultsCountRow}>
              <Typography component="p" className={classes.resultsCountHeading}>
                <LocateFixed
                  className={classes.resultsCountLocateIcon}
                  size={16}
                  aria-hidden
                />
                <span className={classes.resultsCountText}>
                  {resultsCountLine.prefix}
                  <button
                    type="button"
                    className={classes.resultsCountMembershipLink}
                    aria-haspopup="dialog"
                    onClick={() => setOpenAccreditationId(PGA_MEMBER_ACCREDITATION_ID)}
                  >
                    {resultsCountLine.membershipLabel}
                  </button>
                </span>
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12} className={classes.resultsGridSection}>
          <DirectoryHydrateContext.Provider value={isLocationHydrating}>
            <CoachSearchResultCard {...{
              hits,
              currentRefinement,
              refine,
              resultsString: resultsStringFor(currentRefinement, placeSearchValue),
              placeSearchValue,
              savedOnly,
              isCoachSaved,
              onToggleSavedCoach: handleToggleSavedCoach,
              onToggleSavedOnly: handleToggleSavedOnly,
              bookThisWeek,
              bookThisWeekJoinReady,
              onClearDirectoryFilters: onClearDirectoryFilters
                ? handleClearDirectoryFilters
                : undefined,
            }}
            />
          </DirectoryHydrateContext.Provider>
        </Grid>
        {shouldShowBanner && (
          <Grid item xs={12} className={classes.eventBannerGridItem}>
            <EventCardBanner />
          </Grid>
        )}
      </Grid>
      <CoachAccreditationExplainerDialog
        accreditationId={openAccreditationId}
        onClose={closeMembershipExplainer}
      />
    </Container>
  )
}

SearchResults.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  hits: PropTypes.array,
  placeName: PropTypes.string,
  nbHits: PropTypes.number,
  searchState: PropTypes.object,
  persona: PropTypes.string,
  bookThisWeek: PropTypes.bool,
  onPersonaChange: PropTypes.func,
  onToggleBookThisWeek: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
  setAroundLatLng: PropTypes.func,
  aroundLatLng: PropTypes.string,
  bookThisWeekJoinReady: PropTypes.bool,
  searching: PropTypes.bool,
  isSearchStalled: PropTypes.bool,
  searchResults: PropTypes.object,
  refinerData: PropTypes.shape({
    Services: PropTypes.shape({
      serviceTypes: PropTypes.arrayOf(PropTypes.shape({
        typeName: PropTypes.string,
        services: PropTypes.arrayOf(PropTypes.shape({
          serviceName: PropTypes.string,
        })),
      })),
    }),
    Experiences: PropTypes.shape({
      experienceTypes: PropTypes.arrayOf(PropTypes.shape({
        typeName: PropTypes.string,
        experiences: PropTypes.arrayOf(PropTypes.shape({
          experienceName: PropTypes.string,
        })),
      })),
    }),
  }),
}

const SearchBox = compose(
  connectStats,
  connectHits,
  connectSearchBox,
  connectStateResults,
)(SearchResults)

function Pagination(props) {
  const {
    searchClient,
    resultsState,
    onSearchStateChange,
    searchState,
    createURL,
    indexName,
    onSearchParameters,
    aroundLatLng,
    aroundRadius,
    placeName,
    refinerData,
    onPersonaChange,
    persona,
    bookThisWeek,
    onToggleBookThisWeek,
    setAroundLatLng,
    onClearDirectoryFilters,
    bookThisWeekObjectIDs,
  } = props
  const { objectIDs, ready: bookThisWeekJoinReady } = useBookThisWeekDirectory({
    aroundLatLng,
    aroundRadius,
    bookThisWeekOnly: Boolean(bookThisWeek),
    initialObjectIDs: bookThisWeekObjectIDs,
  })
  return (
    <InstantSearch
      searchClient={searchClient}
      resultsState={resultsState}
      onSearchStateChange={onSearchStateChange}
      searchState={searchState}
      createURL={createURL}
      indexName={indexName}
      onSearchParameters={onSearchParameters}
      {...props}
    >
      <ConfigureWithLocation {...{
        aroundLatLng,
        aroundRadius,
        bookThisWeekOnly: Boolean(bookThisWeek),
        objectIDs,
        ready: bookThisWeekJoinReady,
      }}
      />
      <SearchBox {...{
        searchState,
        aroundLatLng,
        placeName,
        refinerData,
        onPersonaChange,
        persona,
        bookThisWeek,
        bookThisWeekJoinReady,
        onToggleBookThisWeek,
        setAroundLatLng,
        onClearDirectoryFilters,
      }}
      />
    </InstantSearch>
  )
}

Pagination.propTypes = {
  searchClient: PropTypes.object,
  resultsState: PropTypes.object,
  onSearchStateChange: PropTypes.func,
  searchState: PropTypes.object,
  createURL: PropTypes.any,
  indexName: PropTypes.string,
  onSearchParameters: PropTypes.func,
  aroundLatLng: PropTypes.string,
  aroundRadius: PropTypes.number,
  placeName: PropTypes.string,
  onPersonaChange: PropTypes.func,
  onToggleBookThisWeek: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
  persona: PropTypes.string,
  bookThisWeek: PropTypes.bool,
  bookThisWeekObjectIDs: PropTypes.arrayOf(PropTypes.string),
  setAroundLatLng: PropTypes.func,
  refinerData: PropTypes.shape({
    Services: PropTypes.shape({
      serviceTypes: PropTypes.arrayOf(PropTypes.shape({
        typeName: PropTypes.string,
        services: PropTypes.arrayOf(PropTypes.shape({
          serviceName: PropTypes.string,
        })),
      })),
    }),
    Experiences: PropTypes.shape({
      experienceTypes: PropTypes.arrayOf(PropTypes.shape({
        typeName: PropTypes.string,
        experiences: PropTypes.arrayOf(PropTypes.shape({
          experienceName: PropTypes.string,
        })),
      })),
    }),
  }),
}

export { default as CoachesSearchFindResultsState } from './CoachesSearchFindResultsState'

export default Pagination
